import { supabase } from '../supabase';
import { CacheService } from './cacheService';

/**
 * Serviço para buscar dados de interações do usuário com jogos e recomendações.
 */
export class UserInteractionDataService {
  constructor(private cacheService: CacheService) {
    // No futuro, poderia receber o cliente Supabase como injeção de dependência se necessário.
    console.log('[UserInteractionDataService constructor] CacheService initialized:', this.cacheService ? 'Instance OK' : 'Instance FAILED');
  }

  /**
   * Obtém os IDs de todos os jogos para os quais um usuário forneceu feedback (liked/disliked).
   * @param userId O ID do usuário.
   * @returns Uma Promise que resolve para um array de IDs de jogos.
   */
  async getAllFeedbackGameIds(userId: string): Promise<string[]> {
    if (!userId) {
      console.warn('[UserInteractionDataService] getAllFeedbackGameIds: userId não fornecido.');
      return [];
    }
    try {
      const { data, error } = await supabase
        .from('recommendations')
        .select('game_id')
        .eq('user_id', userId)
        .not('liked', 'is', null);
        
      if (error) {
        console.error('[UserInteractionDataService] Erro ao buscar jogos com feedback:', error);
        return [];
      }
      
      return data ? data.map(item => item.game_id) : [];
    } catch (error) {
      console.error('[UserInteractionDataService] Exceção ao processar IDs de jogos com feedback:', error);
      return [];
    }
  }

  /**
   * Obtém IDs de todos os jogos com os quais o usuário interagiu de alguma forma (avaliados, na coleção, feedback em recomendações, interações diretas).
   * Lógica movida de UserGameService.
   * @param userId O ID do usuário.
   * @returns Uma Promise que resolve para um array de IDs de jogos únicos.
   */
  public async getAllInteractedGameIds(userId: string): Promise<string[]> {
    if (!userId) {
      console.warn('[UserInteractionDataService] getAllInteractedGameIds: userId não fornecido.');
      return [];
    }
    try {
      // 1. Jogos avaliados (rating não nulo)
      const { data: ratedGames, error: ratingError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .not('rating', 'is', null);
      if (ratingError) {
        console.error('[UserInteractionDataService] Erro ao obter jogos avaliados:', ratingError);
        // Considerar se deve retornar [] ou continuar com outros dados
      }
      const ratedIds = ratedGames ? ratedGames.map(item => item.game_id) : [];

      // 2. Interações explícitas
      const { data: interactions, error: interactionError } = await supabase
        .from('user_interactions')
        .select('game_id')
        .eq('user_id', userId);
      if (interactionError) {
        console.error('[UserInteractionDataService] Erro ao obter interações com jogos:', interactionError);
      }
      const interactionIds = interactions ? interactions.map(item => item.game_id) : [];

      // 3. Recomendações com feedback (usa o método já existente neste serviço)
      const feedbackRecommendationIds = await this.getAllFeedbackGameIds(userId);

      // 4. Jogos na coleção (owned = true)
      const { data: userOwnedGames, error: ownedError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .eq('owned', true);
      if (ownedError) {
        console.error('[UserInteractionDataService] Erro ao obter jogos na coleção:', ownedError);
      }
      const ownedGameIds = userOwnedGames ? userOwnedGames.map(item => item.game_id) : [];

      const combinedIds = [
        ...ratedIds,
        ...interactionIds,
        ...feedbackRecommendationIds,
        ...ownedGameIds
      ];
      
      const uniqueIds = [...new Set(combinedIds)];
      console.log(`[UserInteractionDataService] getAllInteractedGameIds for ${userId}: Encontrados ${uniqueIds.length} IDs únicos de jogos interagidos.`);
      return uniqueIds;

    } catch (error) {
      console.error('[UserInteractionDataService] Exceção em getAllInteractedGameIds:', error);
      return [];
    }
  }

  /**
   * Limpa o cache de recomendações para um usuário específico.
   * Este método deve ser chamado quando a coleção de jogos do usuário muda
   * (ex: adiciona jogo, avalia jogo), para garantir que novas recomendações
   * sejam geradas refletindo essas mudanças.
   * @param userId O ID do usuário para o qual o cache de recomendação será limpo.
   */
  public async clearRecommendationCacheForUser(userId: string): Promise<void> {
    try {
      if (!userId) {
        console.warn('[UserInteractionDataService] clearRecommendationCacheForUser: userId não fornecido.');
        return;
      }
      
      const recCacheKey = `recommendations:${userId}:default`;
      const forceRecCacheKey = `recommendations:${userId}:force`;
      
      console.log(`[UserInteractionDataService] Invalidando cache de recomendações para o usuário ${userId}. Chaves: ${recCacheKey}, ${forceRecCacheKey}`);
      
      await this.cacheService.delete(recCacheKey);
      await this.cacheService.delete(forceRecCacheKey);
      
      console.log(`[UserInteractionDataService] Cache de recomendações invalidado com sucesso para o usuário ${userId}.`);
    } catch (error) {
      console.error(`[UserInteractionDataService] Erro ao limpar cache de recomendações para ${userId}: ${error instanceof Error ? error.message : String(error)}`);
      // Considere relançar o erro ou tratar de forma mais específica se necessário
    }
  }
} 